<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getQuantileIntervals } from '@chatwoot/utils';
import { useMemoize } from '@vueuse/core';
import { useHeatmapTooltip } from './heatmaps/composables/useHeatmapTooltip';
import HeatmapTooltip from './heatmaps/HeatmapTooltip.vue';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';
import { buildWeekdayHourArchiveFilters } from '../helpers/summaryReportArchiveHelper';

const props = defineProps({
  points: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
  title: {
    type: String,
    default: '',
  },
  disableArchive: {
    type: Boolean,
    default: false,
  },
  metricKey: {
    type: String,
    default: 'conversationsCount',
  },
});

const { t } = useI18n();
const { openArchive } = useSummaryReportArchive();
const tooltip = useHeatmapTooltip();

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0];
const SHOW_HOUR_LABEL_INDICES = new Set([0, 3, 6, 9, 12, 15, 18, 21]);

const HOUR_LABELS = [
  '12:00 am',
  '1:00 am',
  '2:00 am',
  '3:00 am',
  '4:00 am',
  '5:00 am',
  '6:00 am',
  '7:00 am',
  '8:00 am',
  '9:00 am',
  '10:00 am',
  '11:00 am',
  '12:00 pm',
  '1:00 pm',
  '2:00 pm',
  '3:00 pm',
  '4:00 pm',
  '5:00 pm',
  '6:00 pm',
  '7:00 pm',
  '8:00 pm',
  '9:00 pm',
  '10:00 pm',
  '11:00 pm',
];

const weekdayLabels = computed(() =>
  WEEKDAY_ORDER.map(day => t(`CHATS_REPORTS.WEEKDAYS.${day}`))
);

const heatmapTitle = computed(
  () => props.title || t('CHATS_REPORTS.CHARTS.HEATMAP')
);

const matrix = computed(() => {
  const grid = HOURS.map(() => WEEKDAY_ORDER.map(() => 0));
  (props.points || []).forEach(point => {
    const weekday = Number(point.weekday);
    const hour = Number(point.hour);
    const col = WEEKDAY_ORDER.indexOf(weekday);
    if (col >= 0 && hour >= 0 && hour < 24) {
      grid[hour][col] += Number(point.value) || 0;
    }
  });
  return grid;
});

const quantileRange = computed(() => {
  const flat = matrix.value.flat();
  return getQuantileIntervals(flat, [0.2, 0.4, 0.6, 0.8, 0.9, 0.99]);
});

const getHeatmapLevelClass = useMemoize((value, quantileRangeArray) => {
  if (!value) {
    return 'border border-n-container bg-n-slate-2 dark:bg-n-slate-1/30';
  }
  let level = [...quantileRangeArray, Infinity].findIndex(
    range => value <= range && value > 0
  );
  if (level > 6) level = 5;
  if (level === 0) {
    return 'border border-n-container bg-n-slate-2 dark:bg-n-slate-1/30';
  }
  return [
    'bg-n-teal-3 border border-n-teal-4/30',
    'bg-n-teal-5 border border-n-teal-6/30',
    'bg-n-teal-7 border border-n-teal-8/30',
    'bg-n-teal-8 border border-n-teal-9/30',
    'bg-n-teal-10 border border-n-teal-8/30',
    'bg-n-teal-11 border border-n-teal-10/30',
  ][level - 1];
});

const getHeatmapClass = value =>
  getHeatmapLevelClass(value, quantileRange.value);

const onCellClick = (weekday, hour, value) => {
  if (!value || props.disableArchive) return;
  const filters = buildWeekdayHourArchiveFilters({
    metricKey: props.metricKey,
    reportType: props.archiveContext?.reportType || 'account',
    context: props.archiveContext,
    weekday,
    hour,
  });
  openArchive({
    metricKey: props.metricKey,
    reportType: props.archiveContext?.reportType || 'account',
    row: null,
    context: props.archiveContext,
    filters,
  });
};
</script>

<template>
  <section
    class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2"
  >
    <div class="mb-0 flex flex-wrap items-center justify-between gap-2">
      <h3 class="mb-0 text-base font-medium text-n-slate-12">
        {{ heatmapTitle }}
      </h3>
      <slot name="actions" />
    </div>
    <div class="mt-4 w-full overflow-x-auto">
      <div class="flex min-w-[420px] flex-row">
        <div class="mt-6 flex w-16 flex-shrink-0 flex-col">
          <div
            v-for="(hour, i) in HOURS"
            :key="hour"
            class="mb-[3px] flex h-5 items-center justify-end pr-2 text-[9px] font-semibold text-n-slate-11"
          >
            <span v-if="SHOW_HOUR_LABEL_INDICES.has(i)">{{
              HOUR_LABELS[i]
            }}</span>
          </div>
        </div>
        <div class="flex flex-1 flex-row gap-[3px]">
          <div
            v-for="(weekday, colIdx) in WEEKDAY_ORDER"
            :key="weekday"
            class="flex min-w-[36px] flex-1 flex-col gap-[3px]"
          >
            <div
              class="flex h-6 items-center justify-center whitespace-nowrap text-[9px] font-semibold text-n-slate-11"
            >
              {{ weekdayLabels[colIdx] }}
            </div>
            <div
              v-for="(hour, rowIdx) in HOURS"
              :key="`${weekday}-${hour}`"
              class="h-5 cursor-pointer rounded-sm"
              :class="
                loading
                  ? 'animate-loader-pulse bg-n-slate-3 dark:bg-n-slate-1'
                  : getHeatmapClass(matrix[rowIdx][colIdx])
              "
              @mouseenter="tooltip.show($event, matrix[rowIdx][colIdx])"
              @mouseleave="tooltip.hide"
              @click="onCellClick(weekday, hour, matrix[rowIdx][colIdx])"
            />
          </div>
        </div>
      </div>
      <HeatmapTooltip
        :visible="tooltip.visible.value"
        :x="tooltip.x.value"
        :y="tooltip.y.value"
        :value="tooltip.value.value"
      />
    </div>
  </section>
</template>
